import { FieldPath, UseFormReturn } from 'react-hook-form';
import { TFunction } from 'i18next';
import {
  isValidCalloutSlug,
  titleToCalloutSlug
} from 'v5/api/callouts/callout-slug.logic';
import { CalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';
import { validate as isUuid } from 'uuid';

export function shouldSkipSlugAvailabilityCheck(value: string): boolean {
  return !value?.trim() || isUuid(value) || !isValidCalloutSlug(value);
}

export function validateCalloutSlugFormat(
  value: string,
  title: string,
  t: TFunction<'callouts'>
): true | string {
  if (!value?.trim()) {
    const derivedFromTitle = titleToCalloutSlug(title);
    if (!derivedFromTitle) {
      return true;
    }
    return t('CalloutDialog.slugRequired');
  }
  if (isUuid(value)) {
    return t('CalloutDialog.slugUuidDisallowed');
  }
  if (!isValidCalloutSlug(value)) {
    return t('CalloutDialog.slugInvalid');
  }
  return true;
}

export function createCalloutSlugAvailabilityValidator<
  T extends { slug: string }
>({
  form,
  slugPath,
  calloutsService,
  excludeCalloutId,
  workspaceId,
  t
}: {
  form: UseFormReturn<T>;
  slugPath: FieldPath<T>;
  calloutsService: CalloutsService;
  excludeCalloutId?: number;
  workspaceId?: number | null;
  t: TFunction<'callouts'>;
}) {
  return async function validateCalloutSlugAvailability(
    value: string
  ): Promise<true | string> {
    if (shouldSkipSlugAvailabilityCheck(value)) {
      return true;
    }

    const slugOptions = {
      excludeCalloutId,
      workspaceId
    };

    const isOccupied = await calloutsService.isSlugOccupied(value, slugOptions);

    const currentValue = String(form.getValues(slugPath) ?? '');
    if (currentValue !== value) {
      if (shouldSkipSlugAvailabilityCheck(currentValue)) {
        return true;
      }
      const currentOccupied = await calloutsService.isSlugOccupied(
        currentValue,
        slugOptions
      );
      return currentOccupied ? t('CalloutDialog.slugDuplicate') : true;
    }

    return isOccupied ? t('CalloutDialog.slugDuplicate') : true;
  };
}
